import { useMutation } from '@apollo/client/react';
import { GENERATE_PLAYBACK_URLS } from '@optools/services/operator-gateway/mutations';
import {
  MilestonesGameQuery,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import Hls from 'hls.js';
import {
  createContext,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'sonner';

import { formatOriginalAssetUrl } from '../utils/milestones';

interface MilestonesGrid {
  scrollToIndexes: (index: number) => void;
}

interface MilestoneEditorContextValue {
  duration: number | null;
  gridRef: MutableRefObject<MilestonesGrid | null>;
  isVOD: boolean;
  playbackUrlsLoading: boolean;
  programDateTime: string;
  seekTo: (videoTime: number) => void;
  toggleTrimMode: () => void;
  trimMode: boolean;
  videoLoading: boolean;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
}

const MilestoneEditorContext = createContext<MilestoneEditorContextValue | null>(null);

export interface MilestoneEditorProviderProps {
  children: ReactNode;
  mediaId: string;
  pipelines: NonNullable<MilestonesGameQuery['game']>['pipelines'];
}

export const MilestoneEditorContextProvider = ({
  children,
  mediaId,
  pipelines,
}: MilestoneEditorProviderProps) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const gridRef = useRef<MilestonesGrid>(null);
  const [programDateTime, setProgramDateTime] = useState('');
  const [duration, setDuration] = useState<number | null>(null);
  const [trimMode, setTrimMode] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);

  const [generatePlaybackUrls, generatePlaybackUrlsStatus] = useMutation(GENERATE_PLAYBACK_URLS, {
    onCompleted: ({ generatePlaybackUrls }) => {
      if (!generatePlaybackUrls?.[0]?.playbacks?.[0]?.url) {
        toast.error('No Playback URL', {
          description: 'No playback URLs found for feed.',
          duration: Infinity,
        });
      }
    },
  });

  useEffect(() => {
    if (!mediaId) return;
    generatePlaybackUrls({ variables: { mediaIds: [mediaId] } });
  }, [generatePlaybackUrls, mediaId]);

  const url =
    generatePlaybackUrlsStatus?.data?.generatePlaybackUrls?.[0]?.playbacks?.[0]?.url ?? '';

  useEffect(() => {
    if (Hls.isSupported() && url) {
      const playbackUrl = trimMode ? formatOriginalAssetUrl(url) : url;

      const hls = new Hls();
      hls.loadSource(playbackUrl);
      hls.attachMedia(videoRef.current as HTMLVideoElement);

      hls.on(Hls.Events.LEVEL_LOADED, (_, data) => {
        setProgramDateTime(data.details.fragments[0].rawProgramDateTime ?? '');

        const duration = data.details.totalduration;
        if (duration) setDuration(Math.floor(duration));
      });

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        setVideoLoading(false);
      });

      // Fall back to the original URL when the manifest is not found due to not being trimmed yet
      hls.on(Hls.Events.ERROR, (_, data) => {
        if (
          data.type === Hls.ErrorTypes.NETWORK_ERROR &&
          data.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR &&
          data.response &&
          data.response.code === 404
        ) {
          hls.loadSource(url);
        }
      });

      return () => {
        hls.destroy();
      };
    }
  }, [url, trimMode]);

  const isVOD = useMemo(() => {
    const pipeline = pipelines.find((pipeline) =>
      pipeline.media.find((media) => media.id === mediaId),
    );

    if (!pipeline) return false;

    return [StreamState.STREAM_DONE, StreamState.EVENT_ARCHIVE].includes(pipeline.streamState);
  }, [mediaId, pipelines]);

  const seekTo = useCallback((videoTime: number) => {
    if (videoRef.current) {
      videoRef.current.currentTime = videoTime;
    }
  }, []);

  const toggleTrimMode = useCallback(() => {
    setTrimMode((current) => !current);
    setDuration(null);
    setProgramDateTime('');
    setVideoLoading(true);
  }, []);

  const value = useMemo(
    () =>
      ({
        duration,
        gridRef,
        isVOD,
        playbackUrlsLoading: generatePlaybackUrlsStatus.loading,
        programDateTime,
        seekTo,
        toggleTrimMode,
        trimMode,
        videoLoading,
        videoRef,
      }) satisfies MilestoneEditorContextValue,
    [
      duration,
      isVOD,
      programDateTime,
      seekTo,
      toggleTrimMode,
      trimMode,
      generatePlaybackUrlsStatus.loading,
      videoLoading,
    ],
  );

  return (
    <MilestoneEditorContext.Provider value={value}>{children}</MilestoneEditorContext.Provider>
  );
};

export const useMilestoneEditor = () => {
  const context = useContext(MilestoneEditorContext);
  if (!context) {
    throw new Error('useMilestoneEditor must be used within a MilestoneEditorContextProvider');
  }

  return context;
};
